import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink, Edit2, CalendarPlus, DollarSign, CreditCard, Send, Copy, Check, AlertTriangle, MoreHorizontal, FileText, Calculator as CalculatorIcon } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/dropdown-menu';
import { doc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import { useRole } from '../../../../context/RoleContext';
import { useTasks } from '../../../../context/TasksContext';
import { auth } from '../../../../firebase';
import { toast } from 'sonner';
import { getStageColorClasses } from '../../../../lib/stageColors';
import { getDisplayName } from '../../../../lib/utils';
import ChargeNowModal from '../../../../components/ChargeNowModal';
import CardStatusBadge from '../../../../components/CardStatusBadge';
import CardSetupModal from '../../../../pages/CardSetupModal';
import CallButton from '../../../../components/clicktocall/CallButton';
import NewLeadModal from '../../../../components/leads/NewLeadModal';
import AccountStatusMenu from './AccountStatusMenu';
import { isClosedAccount } from '../../../../utils/accountLifecycle';

/**
 * ProfileHeader - Account profile header component
 * Displays name, stage, contact info, and action buttons.
 *
 * Works for both Leads and Clients (unified profile).
 * "Schedule Job" navigates to /calendar with the lead pre-selected so the
 * dispatcher can pick a time slot and confirm the booking.
 */
const ProfileHeader = ({ client, entryContext = 'clients' }) => {
  const { companyId } = useCompany();
  const { isTechnician } = useRole();
  const { tasks } = useTasks();
  const navigate = useNavigate();
  const [chargeNowOpen,   setChargeNowOpen]   = useState(false);
  const [cardModalOpen,   setCardModalOpen]   = useState(false);
  const [editOpen,        setEditOpen]        = useState(false);
  const [setupLinkState,  setSetupLinkState]  = useState({
    loading: false,
    sent:    false,
    copied:  false,
    link:    null,
  });

  // Count future scheduled jobs on this lead — surfaces "ghost work" warnings
  // when an account is closed but still has work on the calendar.
  const futureJobCount = useMemo(() => {
    if (!client?.id || !Array.isArray(tasks)) return 0;
    const now = Date.now();
    return tasks.filter((t) => {
      if (t.leadId !== client.id) return false;
      if (t.type !== 'job') return false;
      if (t.status === 'completed' || t.status === 'cancelled') return false;
      const startTs = t.scheduledStart ? new Date(t.scheduledStart).getTime() : NaN;
      return Number.isFinite(startTs) && startTs >= now;
    }).length;
  }, [tasks, client?.id]);

  const showGhostWarning = isClosedAccount(client) && futureJobCount > 0;

  if (!client) return null;

  const cardStatus   = client.cardOnFileStatus || null;
  const hasCard      = cardStatus === 'active' || cardStatus === 'needs_update';
  const cardBtnLabel = hasCard ? 'Update Card' : 'Add Card';
  const cardBtnCls   =
    cardStatus === 'needs_update'
      ? 'text-amber-700 border-amber-300 hover:bg-amber-50'
      : 'text-blue-700 border-blue-300 hover:bg-blue-50';

  // ── Send Card Setup Link ─────────────────────────────────────────────────
  const handleSendSetupLink = async () => {
    setSetupLinkState({ loading: true, sent: false, copied: false, link: null });
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/payments/send-card-setup-link`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, leadId: client.id, sendSms: true }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}

      if (res.ok && data.success) {
        setSetupLinkState({ loading: false, sent: true, copied: false, link: data.setupLink });
        toast.success(data.smsSent ? 'Card setup link sent via SMS' : 'Card setup link generated');
      } else {
        setSetupLinkState({ loading: false, sent: false, copied: false, link: null });
        toast.error(data.detail || 'Failed to send card setup link');
      }
    } catch (err) {
      console.error('[ProfileHeader] Send setup link error:', err);
      setSetupLinkState({ loading: false, sent: false, copied: false, link: null });
      toast.error('Network error — please try again');
    }
  };

  const handleCopySetupLink = () => {
    if (!setupLinkState.link) return;
    navigator.clipboard.writeText(setupLinkState.link)
      .then(() => {
        setSetupLinkState(s => ({ ...s, copied: true }));
        toast.success('Card setup link copied');
        setTimeout(() => setSetupLinkState(s => ({ ...s, copied: false })), 2500);
      })
      .catch(() => toast.error('Failed to copy link'));
  };

  const entityLabel = entryContext === 'leads' ? 'Lead' : 'Client';
  const backPath = entryContext === 'leads' ? '/leads' : '/clients';

  const formatAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      const { streetNumber, streetName, street, city, state, zip } = address;
      const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
      return [streetPart, city, state, zip].filter(Boolean).join(', ');
    }
    return '';
  };

  const handleViewInPipeline = () => {
    // Pipeline reads `highlightLeadId` from location state, scrolls the
    // column into view, and applies a temporary highlight ring on the card.
    navigate('/pipeline', { state: { highlightLeadId: client.id } });
  };

  const handleEditClient = () => {
    setEditOpen(true);
  };

  // Sprint 292 — Lifecycle remediation
  // The former `handleDeleteClient` (raw `deleteDoc(leads/{leadId})`) has been
  // removed. Normal end-of-service is now Close Account (S4 lifecycle
  // endpoint), reachable via the AccountStatusMenu. Rare owner-only hard
  // purge is a separate backend workflow (`POST /api/leads/{leadId}/purge`)
  // and is intentionally not surfaced from this menu — it must never be
  // executed casually from a UI dropdown.

  // Navigate to calendar with lead pre-selected; SchedulerShell detects the
  // state and opens ScheduleJobModal automatically.
  const handleScheduleJob = () => {
    navigate('/calendar', { state: { scheduleLeadId: client.id } });
  };

  // Launch existing Quote workspace with this customer pre-loaded. The
  // `leadId` query param ties the new quote to this profile (no duplicate
  // lead is created — see QuoteWorkspace.persistQuote).
  const handleNewQuote = () => {
    navigate(`/quotes/new?leadId=${client.id}`);
  };

  // Launch existing Calculator with this customer pre-loaded. Calculator
  // reads the same `leadId` URL param to prefill client info and re-center
  // the property map on the verified location.
  const handleOpenCalculator = () => {
    navigate(`/calculator?leadId=${client.id}`);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Left: Client Info */}
        <div className="space-y-4 flex-1 min-w-0">
          {/* Name and Stage */}
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-gray-900">{getDisplayName(client, 'Unnamed')}</h1>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStageColorClasses(client.stage)}`}>
              {client.stage}
            </span>
            {/* Account Lifecycle pill (Phase A — UX only, no enforcement yet) */}
            <AccountStatusMenu lead={client} readOnly={isTechnician} />
          </div>

          {showGhostWarning && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              data-testid="closed-account-ghost-jobs-warning"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
              <span>
                This account is <strong>Closed</strong> but still has {futureJobCount} scheduled future job{futureJobCount === 1 ? '' : 's'}.
                Review or cancel them on the Calendar to avoid ghost work.
              </span>
            </div>
          )}

          {/* Contact Information */}
          <div className="space-y-2">
            {formatAddress(client.address) && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="text-sm">{formatAddress(client.address)}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="h-4 w-4 shrink-0" />
                {/* Shared CallButton — auto-routes browser vs. phone
                    bridge; KRO caller-ID always shown. */}
                <CallButton
                  phone={client.phone}
                  surface="client-profile-header"
                  className="text-sm text-emerald-700 hover:underline font-medium inline-flex items-center"
                  title="Call client"
                  data-testid="client-profile-call-btn"
                >
                  {client.phone}
                </CallButton>
              </div>
            )}
            {client.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="h-4 w-4 shrink-0" />
                <a href={`mailto:${client.email}`} className="text-sm hover:text-blue-600">
                  {client.email}
                </a>
              </div>
            )}
          </div>

          {client.source && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Source:</span> {client.source}
            </div>
          )}

          {/* Card on File status — shown inline so operators have instant context */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">Card on File:</span>
            <CardStatusBadge
              cardOnFileStatus={cardStatus}
              cardBrand={client.cardBrand}
              cardLast4={client.cardLast4}
            />
          </div>
        </div>

        {/* Right: Action Buttons — hidden for technicians (read-only field view) */}
        {/*
         * Phase C (iter-270) — header was reduced from 7 always-visible buttons
         * to 3 primary actions + an overflow menu, per the audit table in
         * /app/memory/CHANGELOG.md. Behavior is unchanged: every handler is
         * the same, every modal still mounts here. Demotions:
         *   Send Card Setup Link · View in Pipeline · Edit · Delete → overflow.
         * Primary row stays Charge Now · Schedule Job · Add Card.
         */}
        {!isTechnician && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              onClick={() => setChargeNowOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="lead-charge-now-btn"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Charge Now
            </Button>

            <Button
              onClick={handleScheduleJob}
              className="text-white"
              data-testid="schedule-job-btn"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Schedule Job
            </Button>

            {/* Quote — launches existing QuoteWorkspace with leadId context
                so customer info is prefilled and the resulting quote stays
                attached to this customer record. No duplicate lead is
                created (see QuoteWorkspace.persistQuote). */}
            <Button
              variant="outline"
              onClick={handleNewQuote}
              className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
              data-testid="profile-new-quote-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Quote
            </Button>

            <Button
              variant="outline"
              onClick={() => setCardModalOpen(true)}
              className={cardBtnCls}
              data-testid="add-update-card-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {cardBtnLabel}
            </Button>

            {/* Inline Copy chip — appears after Send Card Setup Link has been
                triggered from the overflow menu. Kept inline (option b from the
                Phase C audit) so the operator can still grab the link if the
                SMS bounced — same as the pre-Phase-C behavior, just relocated. */}
            {setupLinkState.sent && setupLinkState.link && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySetupLink}
                className="text-gray-600 border-gray-300 hover:bg-gray-50"
                data-testid="copy-card-setup-link-btn"
                title="Copy card setup link"
              >
                {setupLinkState.copied
                  ? <><Check className="h-4 w-4 mr-1.5 text-green-600" /> Copied</>
                  : <><Copy className="h-4 w-4 mr-1.5" /> Copy link</>}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="text-gray-700 border-gray-300 hover:bg-gray-50 px-2.5"
                  data-testid="profile-header-overflow-btn"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56"
                data-testid="profile-header-overflow-menu"
              >
                <DropdownMenuItem
                  onSelect={handleSendSetupLink}
                  disabled={setupLinkState.loading}
                  data-testid="send-card-setup-link-btn"
                  className={setupLinkState.sent ? 'text-green-700 focus:text-green-700' : 'text-indigo-700 focus:text-indigo-700'}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {setupLinkState.loading
                    ? 'Sending…'
                    : setupLinkState.sent
                      ? 'Link Sent'
                      : 'Send Card Setup Link'}
                </DropdownMenuItem>
                {/* Calculator — secondary launcher. Primary CTA for existing
                    customers is Quote; Calculator is here for measurement /
                    re-estimating workflows on already-known accounts. */}
                <DropdownMenuItem
                  onSelect={handleOpenCalculator}
                  data-testid="profile-open-calculator-btn"
                >
                  <CalculatorIcon className="h-4 w-4 mr-2" />
                  Open Calculator
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleViewInPipeline}
                  data-testid="view-in-pipeline-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View in Pipeline
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleEditClient}
                  data-testid="edit-lead-btn"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit {entityLabel}
                </DropdownMenuItem>
                {/* Sprint 292 — the former "Delete Client" item has been
                    removed. End-of-service is now "Close Account" from the
                    AccountStatusMenu (green pill button above), which
                    preserves history and cancels future work via the S4
                    lifecycle endpoint. Rare owner-only hard purge is a
                    separate backend workflow and is not surfaced here. */}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>

    {/* Charge Now Modal — manual collection from lead/client profile */}
    <ChargeNowModal
      isOpen={chargeNowOpen}
      onClose={() => setChargeNowOpen(false)}
      companyId={companyId}
      leadId={client.id}
      customerName={getDisplayName(client, 'Client')}
      onSuccess={() => setChargeNowOpen(false)}
    />

    {/* Add / Update Card on File — standalone, no quote context required */}
    {cardModalOpen && (
      <CardSetupModal
        companyId={companyId}
        leadId={client.id}
        mode="setup"
        onSuccess={(card) => {
          setCardModalOpen(false);
          const detail = card?.brand && card?.last4
            ? `${card.brand} ••••${card.last4} saved`
            : 'Card saved';
          toast.success(`Card on file updated — ${detail}`);
        }}
        onClose={() => setCardModalOpen(false)}
      />
    )}

    {/* Edit Lead / Client — reuses shared NewLeadModal in edit mode so the
        same validation + property-details editor are applied everywhere. */}
    <NewLeadModal
      isOpen={editOpen}
      onClose={() => setEditOpen(false)}
      mode="edit"
      lead={client}
    />
    </>
  );
};

export default ProfileHeader;
